// The helm keys of the season map (R2707, ui-map-interface.md §9 "The
// helm keys"). Pure, so a test drives it with a clock of its own.
//
// The helm view of the sea view steers one hull with the STEER order
// (numbers.md §4.2, R2638): a wanted heading and a step of the engine
// telegraph, under a lease that each send renews. The map takes the same
// order with the arrow keys of `[keys.card]`, while one own fleet at sea
// is selected (no flag, R2830). The keys work as the keys of the helm
// view (`seaview/src/helm/steer.ts`, R2671, R3237):
//
//   * left and right turn the rudder while they are held, port and
//     starboard. Each frame the ordered heading turns at
//     `MAP_HELM.rudderRateShare` of the class turn rate, and the rate
//     builds from 0 over `rudderRampS` of the press: a tap turns a degree
//     or so, and a held key turns smoothly. The ordered heading never
//     leads the bow by more than `leadMaxDeg`. The heading is the sim's
//     atan2 sense, +x east and y to the south, so a larger angle turns
//     the bow to starboard.
//   * up and down step the telegraph from 0 (STOP) to `topStep` (FULL),
//     once a press: the repeat of a held key does not step it.
//   * the first key starts from the bow and from the step of the speed,
//     so a turn does not stop a hull that runs (`stepOf`, the rule of the
//     helm view).
//
// A change goes at most once each `sendMs`, and the order goes again each
// `refreshSec`, inside the lease. The helm keeps one record a fleet, so a
// hull keeps its run when the commander selects another. A record lets go
// when the wire shows its fleet on another order after it showed STEER (a
// new order of the commander), when the wire never shows STEER within
// `confirmSec` (a refusal), or when the fleet is gone. Then nothing more
// goes, and the lease of the server ends the last order: the hull holds
// where it brakes to rest, as a closed tab leaves it.

import { Mission } from '../gen/proto/aquaphobia';
import { MAP_HELM } from '../gen/presentation';
import { wrapAngle } from '../render/bow';

/** The helm keys, the ids of `[keys.card]` group `helm`. */
export type HelmKey = 'steer_port' | 'steer_starboard' | 'telegraph_up' | 'telegraph_down';

/** What the helm reads of one own fleet at sea. */
export interface HelmFleet {
  id: number;
  /** The bow, radians in the atan2 sense of the sim. */
  headingRad: number;
  /** The speed now and the top speed of the class, px a wall second. */
  speedPxS: number;
  topPxS: number;
  /** The standing order on the wire (`Mission`). */
  mission: number;
  /** The class turn rate, radians a wall second (`turnRadS`). */
  turnRadS: number;
}

/** The figures of the helm, `MAP_HELM` of the pack. */
export type HelmCfg = { readonly [K in keyof typeof MAP_HELM]: number };

/** One fleet under the helm keys. */
interface HelmRecord {
  heading: number;
  step: number;
  /** A change waits to go. */
  dirty: boolean;
  /** When the order went last, ms; -Infinity before the first send. */
  sentAt: number;
  /** When the wire last showed the fleet on STEER, or the first press. */
  seenAt: number;
  /** The wire showed the fleet on STEER at least once. */
  confirmed: boolean;
}

/** The telegraph step of a hull at `speedPxS` of a top speed of `topPxS`. */
export function stepOf(speedPxS: number, topPxS: number, cfg: HelmCfg = MAP_HELM): number {
  if (!(topPxS > 0) || !(speedPxS > 0)) return 0;
  const share = speedPxS / topPxS;
  if (share < cfg.stopShare) return 0;
  return Math.min(cfg.topStep, Math.max(1, Math.round(share * cfg.topStep)));
}

/** The turn rate of a class, radians a wall second: `turn_rate_deg_s` is
 *  degrees a MAP second, and the map clock runs `tactical` map seconds a
 *  wall second (the rule of the helm view). */
export function turnRadS(turnDegS: number, tactical: number): number {
  return (Math.max(0, turnDegS) * Math.PI / 180) * Math.max(0, tactical);
}

/** The heading the card states: whole degrees of the compass, 0 north,
 *  90 east, from the sim's atan2 angle with y to the south. */
export function compassDeg(rad: number): number {
  const deg = Math.round((rad * 180) / Math.PI + 90);
  return ((deg % 360) + 360) % 360;
}

/** Sends one STEER: the fleet, the heading in milliradians, the step. */
export type SteerSend = (fleetId: number, headingMrad: number, step: number) => void;

export class MapHelm {
  private readonly recs = new Map<number, HelmRecord>();
  /** The held rudder keys, and the fleet they turn. */
  private readonly held = new Set<HelmKey>();
  private rudderId = 0;
  /** The side of the held rudder, -1 port, 1 starboard, and its seconds. */
  private side = 0;
  private heldS = 0;
  /** The clock of the last frame, ms. */
  private lastMs = Number.NaN;

  constructor(private readonly send: SteerSend, private readonly cfg: HelmCfg = MAP_HELM) {}

  /**
   * One press of a helm key on a fleet. It always takes the press. A turn
   * key holds the rudder until `lift`; `repeat` is the repeat of a held
   * key, which does not step the telegraph again.
   */
  press(f: HelmFleet, key: HelmKey, nowMs: number, repeat = false): void {
    if (key === 'steer_port' || key === 'steer_starboard') {
      this.held.add(key);
      this.rudderId = f.id;
      this.begin(f, nowMs);
      return;
    }
    if (repeat) return;
    const r = this.begin(f, nowMs);
    r.step = key === 'telegraph_up' ? Math.min(this.cfg.topStep, r.step + 1) : Math.max(0, r.step - 1);
    r.dirty = true;
    this.flush(f.id, r, nowMs);
  }

  /** A helm key is released; no key lifts every key (the page lost the focus). */
  lift(key?: HelmKey): void {
    if (key) this.held.delete(key);
    else this.held.clear();
  }

  /**
   * One frame: turns the ordered heading of the held rudder, lets go of a
   * record whose fleet left STEER or is gone, and sends a waiting change
   * and the refresh. `look` reads a fleet, or null.
   */
  tick(nowMs: number, look: (id: number) => HelmFleet | null): void {
    const dtS = Number.isNaN(this.lastMs) ? 0 : Math.max(0, nowMs - this.lastMs) / 1000;
    this.lastMs = nowMs;
    this.rudder(dtS, nowMs, look);
    for (const [id, r] of this.recs) {
      const f = look(id);
      if (!f) { this.recs.delete(id); continue; }
      if (f.mission === Mission.MISSION_STEER) {
        r.confirmed = true;
        r.seenAt = nowMs;
      } else if (r.confirmed || nowMs - r.seenAt > this.cfg.confirmSec * 1000) {
        this.recs.delete(id);
        continue;
      }
      this.flush(id, r, nowMs);
    }
  }

  /** The ordered heading in compass degrees and the step of a fleet under
   *  the helm keys, for the card; null for a fleet the helm does not hold. */
  read(fleetId: number): { headingDeg: number; step: number; top: number } | null {
    const r = this.recs.get(fleetId);
    return r ? { headingDeg: compassDeg(r.heading), step: r.step, top: this.cfg.topStep } : null;
  }

  /** The helm holds this fleet. */
  holds(fleetId: number): boolean {
    return this.recs.has(fleetId);
  }

  /** The record of a fleet; the first key starts from the bow and the step of the speed. */
  private begin(f: HelmFleet, nowMs: number): HelmRecord {
    let r = this.recs.get(f.id);
    if (!r) {
      r = {
        heading: wrapAngle(f.headingRad), step: stepOf(f.speedPxS, f.topPxS, this.cfg),
        dirty: true, sentAt: -Infinity, seenAt: nowMs, confirmed: false,
      };
      this.recs.set(f.id, r);
    }
    return r;
  }

  /** The rudder for one frame of `dtS` seconds (the rule of the helm view, R2671). */
  private rudder(dtS: number, nowMs: number, look: (id: number) => HelmFleet | null): void {
    const side = (this.held.has('steer_starboard') ? 1 : 0) - (this.held.has('steer_port') ? 1 : 0);
    if (side !== this.side) this.heldS = 0;
    this.side = side;
    if (side === 0 || !(dtS > 0)) return;
    const f = look(this.rudderId);
    if (!f || !(f.turnRadS > 0)) return;
    const r = this.begin(f, nowMs);
    const c = this.cfg;
    this.heldS += dtS;
    // The rate builds over the ramp of the press, from 0 to the share of the class rate.
    const ramp = c.rudderRampS > 0 ? Math.min(1, this.heldS / c.rudderRampS) : 1;
    const lead = (c.leadMaxDeg * Math.PI) / 180;
    // The lead from the bow, unwrapped by the step, so a turn past the bound stops at it.
    const ahead = wrapAngle(r.heading - f.headingRad) + side * f.turnRadS * c.rudderRateShare * ramp * dtS;
    r.heading = wrapAngle(f.headingRad + Math.min(lead, Math.max(-lead, ahead)));
    r.dirty = true;
  }

  private flush(id: number, r: HelmRecord, nowMs: number): void {
    const since = nowMs - r.sentAt;
    if (r.dirty ? since < this.cfg.sendMs : since < this.cfg.refreshSec * 1000) return;
    r.dirty = false;
    r.sentAt = nowMs;
    this.send(id, Math.round(wrapAngle(r.heading) * 1000), r.step);
  }
}
